/**
 * Navigation Item Component
 * Single menu item with tooltip support in collapsed mode
 */

import { Link, useTranslation } from '@spcc/core'
import {
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  ChevronLeft,
  ChevronRight,
} from '@spcc/uikit'
import type { NavItemProps } from '../types'

// ============================================
// Helper Functions
// ============================================

function getNavItemStyles(collapsed: boolean, isActive: boolean): string {
  const baseStyles = 'w-full flex items-center rounded-xl text-sm font-medium transition-colors duration-200'
  const layoutStyles = collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3.5'
  const activeStyles = 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground shadow-lg shadow-primary/25'
  const inactiveStyles = 'text-muted-foreground hover:bg-secondary hover:text-foreground'

  return cn(baseStyles, layoutStyles, isActive ? activeStyles : inactiveStyles)
}

// ============================================
// Sub-Components
// ============================================

interface TooltipWrapperProps {
  children: React.ReactNode
  label: string
  collapsed: boolean
  isRTL: boolean
}

function TooltipWrapper({ children, label, collapsed, isRTL }: TooltipWrapperProps) {
  if (!collapsed) return <>{children}</>

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children as any}</TooltipTrigger>
      <TooltipContent side={isRTL ? 'left' : 'right'} className="font-medium">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

// ============================================
// Component
// ============================================

export function NavItem({
  item,
  collapsed,
  isActive,
  isRTL,
  onNavigate,
}: NavItemProps) {
  const { t } = useTranslation()
  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight
  const label = t(item.labelKey)

  return (
    <TooltipWrapper label={label} collapsed={collapsed} isRTL={isRTL}>
      <Link
        to={item.to}
        onClick={onNavigate}
        className={getNavItemStyles(collapsed, isActive)}
      >
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 text-start truncate">{label}</span>
            {isActive && <ChevronIcon className="w-4 h-4" />}
          </>
        )}
      </Link>
    </TooltipWrapper>
  )
}
